import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../features/AuthContext';

// Export ChatUser so other files can use it
export interface ChatUser {
    id?: number;
    wa_id: string;
    name: string;
    profile_name?: string | null;
    profile_pic_url?: string | null;
    country_code?: string | null;
    language_code?: string | null;
    source?: string | null;
    is_blocked?: boolean;
    is_active?: boolean;
    last_message_at?: string | null;
    last_incoming_at?: string | null;
    last_outgoing_at?: string | null;
    last_seen_at?: string | null;
    extra_data?: unknown;
    created_at?: string | null;
    updated_at?: string | null;
    last_message?: string;
    avatarUrl?: string;
    unread_count?: number;
    profile?: {
        userId?: string;
        name?: string;
        email?: string;
        phone?: string;
        gender?: string;
        age?: number;
        status?: string;
    } | null;
}

export interface ChatMessage {
    _id: string;
    wa_id: string;
    from: string;
    to: string;
    type: "text" | "image" | "document" | "video";
    text: string;
    timestamp: string;
    sender: "user" | "admin";
    is_read?: number;
    is_delivered?: number;
    message: string;
    name?: string;
    messageId?: string;
    message_id?: string;
    status?: string;
    provider_response?: unknown;
    body?: string;
    full_message?: string;
    caption?: string | null;
    media_url?: string | null;
    media_mime_type?: string | null;
    message_type?: string;
    template_name?: string;
    template_language?: string;
    template_variables?: Record<string, unknown>;
    target_message_id?: string;
    reactions?: Array<{
        emoji: string;
        wa_id?: string;
        reacted_at?: string;
        reaction_message_id?: string;
    }>;
    reply_to_message_id?: string | null;
    reply_to_wa_id?: string | null;
    is_forwarded?: boolean;
    is_frequently_forwarded?: boolean;
    reply_to_message?: ChatMessage;
}

export interface WhatsappTemplateParameter {
    key: string;
    label: string;
    required: boolean;
    default?: string;
}

export interface WhatsappTemplateSchema {
    id?: number;
    key?: string;
    template_key?: string;
    template_name: string;
    language_code: string;
    category?: string;
    status?: string;
    description?: string;
    source?: string;
    is_automatic?: boolean;
    trigger_type?: "automatic" | "manual" | string;
    trigger_source?: string;
    body?: string;
    message?: string;
    message_template?: string;
    full_message?: string;
    parameters: WhatsappTemplateParameter[];
    parameters_json?: WhatsappTemplateParameter[];
    components_json?: Array<Record<string, unknown>>;
    buttons_json?: Array<Record<string, unknown>>;
    meta_template_id?: string;
}

export interface WhatsappSocketError {
    title: string;
    message: string;
    code?: number;
}

export interface WhatsappUnreadSummary {
    [key: string]: any;
}

export interface TemplateMessageRecord {
    _id?: string;
    id?: string | number;
    message_id?: string;
    template_key?: string;
    template_name?: string;
    recipient_user_id?: string | number;
    recipient_phone?: string;
    recipient_name?: string;
    source?: string;
    status?: string;
    message?: string;
    language_code?: string;
    template_parameters?: Record<string, unknown>;
    parameters?: Record<string, string>;
    provider_response?: unknown;
    created_at?: string;
    updated_at?: string;
    delivered_at?: string;
    read_at?: string;
    failed_at?: string;
    error_code?: string | number;
    error_message?: string;
    [key: string]: unknown;
}

export interface TemplateMessagesFilters {
    template_key?: string;
    recipient_user_id?: string;
    recipient_phone?: string;
    source?: string;
    status?: string;
    category?: string;
}

interface WhatsappContextType {
    socket: WebSocket | null;
    users: ChatUser[];
    isConnected: boolean;
    totalUnreadCount: number;
    templates: Record<string, WhatsappTemplateSchema>;
    templatesCount: number;
    unreadSummary: WhatsappUnreadSummary | null;
    socketError: WhatsappSocketError | null;
    selectedUser: ChatUser | null;
    whatsappMessages: ChatMessage[];
    usersLoading: boolean;
    usersHasMore: boolean;
    userSearch: string;
    templateMessages: TemplateMessageRecord[];
    templateMessagesTotal: number;
    templateMessagesLoading: boolean;
    selectUser: (user: ChatUser) => void;
    clearSelectedUser: () => void;
    sendMessage: (text: string) => void;
    startNewChat: (user: ChatUser) => Promise<boolean>;
    sendWhatsappTemplate: (args: { user: ChatUser; templateKey: string; parameters: Record<string, string> }) => Promise<boolean>;
    refreshUsers: () => void;
    fetchTemplates: () => void;
    fetchUnreadSummary: () => void;
    searchUsers: (search: string) => void;
    loadMoreUsers: () => void;
    clearSocketError: () => void;
    setUsers: React.Dispatch<React.SetStateAction<ChatUser[]>>;
    fetchTemplateMessages: (filters?: TemplateMessagesFilters, limit?: number, offset?: number) => void;
}

const WhatsappContext = createContext<WhatsappContextType | undefined>(undefined);

export const WhatsappProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const [socket, setSocket] = useState<WebSocket | null>(null);
    const [users, setUsers] = useState<ChatUser[]>([]);
    const [isConnected, setIsConnected] = useState(false);
    const [templates, setTemplates] = useState<Record<string, WhatsappTemplateSchema>>({});
    const [templatesCount, setTemplatesCount] = useState(0);
    const [unreadSummary, setUnreadSummary] = useState<WhatsappUnreadSummary | null>(null);
    const [socketError, setSocketError] = useState<WhatsappSocketError | null>(null);
    const [selectedUser, setSelectedUser] = useState<ChatUser | null>(null);
    const [whatsappMessages, setWhatsappMessages] = useState<ChatMessage[]>([]);
    const [reconnectSignal, setReconnectSignal] = useState(0);
    const [userSearch, setUserSearch] = useState("");
    const [usersLoading, setUsersLoading] = useState(false);
    const [usersHasMore, setUsersHasMore] = useState(true);
    const [usersOffset, setUsersOffset] = useState(0);
    const [templateMessages, setTemplateMessages] = useState<TemplateMessageRecord[]>([]);
    const [templateMessagesTotal, setTemplateMessagesTotal] = useState(0);
    const [templateMessagesLoading, setTemplateMessagesLoading] = useState(false);
    const usersLimitRef = useRef(10);
    const socketRef = useRef<WebSocket | null>(null);
    const selectedUserRef = useRef<ChatUser | null>(null);
    const usersRequestRef = useRef<{ search: string; offset: number; append: boolean }>({ search: "", offset: 0, append: false });

    // Calculate total unread (sum of all users' unreadCount)
    const totalUnreadCount = users.reduce((acc, user) => acc + (user.unread_count || 0), 0);

    useEffect(() => {
        const storedUser = localStorage.getItem("currentSelectedUser");
        if (!storedUser) return;

        try {
            setSelectedUser(JSON.parse(storedUser));
        } catch (error) {
            console.error("Failed to parse stored selected WhatsApp user", error);
        }
    }, []);

    useEffect(() => {
        selectedUserRef.current = selectedUser;
    }, [selectedUser]);

    const buildEnvelope = (event: string, payload: Record<string, unknown> = {}) => ({
        event,
        action: event,
        type: event,
        data: payload,
    });

    const sendWsEvent = (event: string, payload: Record<string, unknown> = {}) => {
        const ws = socketRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) return;
        ws.send(JSON.stringify(buildEnvelope(event, payload)));
    };

    const normalizeMessageType = (messageType: string): "text" | "image" | "document" | "video" => {
        if (messageType === "image" || messageType === "document" || messageType === "video") {
            return messageType;
        }
        return "text";
    };

    const mapServerMessageToChatMessage = (message: any): ChatMessage => {
        const messageType = normalizeMessageType(message?.message_type || message?.type || "text");
        const mediaUrl = message?.media_url || null;
        const bodyValue = message?.body || "";
        const captionValue = message?.caption || null;
        const textValue = bodyValue || message?.text || message?.message || captionValue || mediaUrl || "";
        const senderValue = message?.sender === "admin" || message?.direction === "outbound" ? "admin" : "user";
        const timestampValue = message?.created_at || message?.timestamp || new Date().toISOString();

        const targetMessageId =
            message?.target_message_id ||
            message?.provider_response?.reaction?.message_id ||
            message?.reaction?.message_id;

        return {
            _id: (message?._id || message?.id || `msg-${Date.now()}`).toString(),
            wa_id: message?.wa_id || "",
            from: message?.sender || message?.from || senderValue,
            to: message?.recipient || message?.to || "",
            type: messageType,
            text: textValue,
            timestamp: timestampValue,
            sender: senderValue,
            is_read: message?.is_read ? 1 : 0,
            is_delivered: message?.is_delivered ? 1 : 0,
            message: textValue,
            messageId: message?.messageId || message?.message_id,
            message_id: message?.message_id || message?.messageId,
            status: message?.status,
            provider_response: message?.provider_response,
            body: bodyValue,
            full_message: message?.full_message || message?.message_body || message?.resolved_message,
            caption: captionValue,
            media_url: mediaUrl,
            media_mime_type: message?.media_mime_type || null,
            message_type: message?.message_type || message?.type,
            template_name: message?.template_name || message?.template_key,
            template_language: message?.template_language || message?.language_code,
            template_variables: message?.template_variables || message?.template_parameters || message?.parameters,
            target_message_id: targetMessageId || undefined,
            reactions: Array.isArray(message?.reactions) ? message.reactions : [],
            reply_to_message_id: message?.reply_to_message_id ?? null,
            reply_to_wa_id: message?.reply_to_wa_id ?? null,
            is_forwarded: Boolean(message?.is_forwarded),
            is_frequently_forwarded: Boolean(message?.is_frequently_forwarded),
            reply_to_message: message?.reply_to_message
        };
    };

    const applyReactionOnMessages = (messages: ChatMessage[], reactionPayload: {
        target_message_id?: string;
        reactions?: Array<{ emoji: string; wa_id?: string; reacted_at?: string; reaction_message_id?: string }>;
        reaction?: { emoji?: string; wa_id?: string; message_id?: string };
        reaction_message_id?: string;
    }): ChatMessage[] => {
        const targetId = reactionPayload?.target_message_id || reactionPayload?.reaction?.message_id;
        if (!targetId) return messages;

        return messages.map((message) => {
            if (message.message_id !== targetId && message.messageId !== targetId) return message;

            if (Array.isArray(reactionPayload.reactions)) {
                return { ...message, reactions: reactionPayload.reactions };
            }

            if (reactionPayload.reaction?.emoji) {
                const existing = Array.isArray(message.reactions) ? message.reactions : [];
                const withoutSameUser = existing.filter((r) => r.wa_id !== reactionPayload.reaction?.wa_id);
                return {
                    ...message,
                    reactions: [
                        ...withoutSameUser,
                        {
                            emoji: reactionPayload.reaction.emoji,
                            wa_id: reactionPayload.reaction.wa_id,
                            reaction_message_id: reactionPayload.reaction_message_id
                        }
                    ]
                };
            }

            return message;
        });
    };

    const normalizeConversationMessages = (messages: ChatMessage[]): ChatMessage[] => {
        const baseMessages = messages.filter((message) => message.message_type !== "reaction");
        const reactionMessages = messages.filter((message) => message.message_type === "reaction");

        let withReactions = [...baseMessages];
        reactionMessages.forEach((reactionMessage) => {
            withReactions = applyReactionOnMessages(withReactions, {
                target_message_id: reactionMessage.target_message_id,
                reaction: {
                    emoji: (reactionMessage.provider_response as any)?.reaction?.emoji,
                    wa_id: reactionMessage.wa_id,
                    message_id: reactionMessage.target_message_id
                },
                reaction_message_id: reactionMessage.message_id
            });
        });

        return withReactions;
    };

    const mapServerUserToChatUser = (user: any): ChatUser => {
        const waId = (user?.wa_id || user?._id || "").toString();
        const displayName = user?.name || user?.profile_name || waId;
        return {
            id: user?.id,
            wa_id: waId,
            name: displayName,
            profile_name: user?.profile_name ?? null,
            profile_pic_url: user?.profile_pic_url ?? null,
            country_code: user?.country_code ?? null,
            language_code: user?.language_code ?? null,
            source: user?.source ?? null,
            is_blocked: user?.is_blocked,
            is_active: user?.is_active,
            last_message_at: user?.last_message_at ?? null,
            last_incoming_at: user?.last_incoming_at ?? null,
            last_outgoing_at: user?.last_outgoing_at ?? null,
            last_seen_at: user?.last_seen_at ?? null,
            extra_data: user?.extra_data ?? null,
            created_at: user?.created_at ?? null,
            updated_at: user?.updated_at ?? null,
            last_message: user?.last_message || user?.last_message || "",
            avatarUrl: user?.avatarUrl || user?.profile_pic_url,
            unread_count: typeof user?.unread_count === "number" ? user.unread_count : 0,
            profile: user?.profile ?? null
        };
    };

    const sortUsersByLatest = (list: ChatUser[]): ChatUser[] => {
        return [...list].sort((a, b) => {
            const aTime = new Date(a.updated_at || a.last_message_at || a.last_seen_at || 0).getTime();
            const bTime = new Date(b.updated_at || b.last_message_at || b.last_seen_at || 0).getTime();
            return bTime - aTime;
        });
    };

    const handleUsersResponse = (payload: any) => {
        const usersPayload = payload?.users || payload?.data?.users;
        const unreadSummaryPayload = payload?.unread_summary || payload?.data?.unread_summary;
        if (unreadSummaryPayload) {
            setUnreadSummary(unreadSummaryPayload);
        }
        if (Array.isArray(usersPayload)) {
            const mappedUsers = usersPayload.map(mapServerUserToChatUser);
            const req = usersRequestRef.current;

            setUsers(prev => {
                if (!req.append) return sortUsersByLatest(mappedUsers);

                const merged = [...prev];
                mappedUsers.forEach((u) => {
                    if (!merged.find(x => x.wa_id === u.wa_id)) merged.push(u);
                });
                return sortUsersByLatest(merged);
            });

            const total =
                payload?.total ??
                payload?.data?.total ??
                payload?.count ??
                payload?.data?.count;
            if (typeof total === "number") {
                setUsersHasMore(req.offset + mappedUsers.length < total);
            } else {
                setUsersHasMore(mappedUsers.length >= usersLimitRef.current);
            }

            setUsersOffset(req.offset);
            setUsersLoading(false);
        }
    };

    const handleMessagesResponse = (payload: any) => {
        const messagesPayload = payload?.messages || payload?.message || payload?.data?.messages || payload?.data?.message;
        if (Array.isArray(messagesPayload)) {
            const mapped = messagesPayload.map(mapServerMessageToChatMessage);
            setWhatsappMessages(normalizeConversationMessages(mapped));
        }
    };

    const handleTemplatesResponse = (payload: any) => {
        const templatesPayload = payload?.templates || payload?.data?.templates;
        const countPayload = payload?.count || payload?.data?.count;

        if (Array.isArray(templatesPayload)) {
            const mappedTemplates = templatesPayload.reduce((acc: Record<string, WhatsappTemplateSchema>, item: any) => {
                const key = item?.key || item?.template_key || item?.template_name;
                if (!key) return acc;
                acc[key] = item;
                return acc;
            }, {});
            setTemplates(mappedTemplates);
        } else if (templatesPayload && typeof templatesPayload === "object") {
            setTemplates(templatesPayload as Record<string, WhatsappTemplateSchema>);
        } else {
            setTemplates({});
        }

        if (typeof countPayload === "number") {
            setTemplatesCount(countPayload);
        } else if (templatesPayload && typeof templatesPayload === "object") {
            setTemplatesCount(Object.keys(templatesPayload).length);
        } else {
            setTemplatesCount(0);
        }
    };

    const handleUnreadSummaryResponse = (payload: any) => {
        const summary = payload?.data || payload;
        if (summary && typeof summary === "object") {
            setUnreadSummary(summary);
        }
    };

    const handleNewMessage = (messagePayload: any) => {
        const normalizedMessage = mapServerMessageToChatMessage(messagePayload);
        if (!normalizedMessage.wa_id) return;

        setUsers(prev => {
            const isSelectedUser = selectedUserRef.current?.wa_id === normalizedMessage.wa_id;
            const existingUser = prev.find(user => user.wa_id === normalizedMessage.wa_id);

            if (!existingUser) {
                const newUser: ChatUser = {
                    wa_id: normalizedMessage.wa_id,
                    name: messagePayload.name || normalizedMessage.wa_id,
                    last_message: normalizedMessage.text,
                    unread_count: isSelectedUser ? 0 : 1,
                    updated_at: normalizedMessage.timestamp,
                    last_message_at: normalizedMessage.timestamp
                };
                return sortUsersByLatest([newUser, ...prev]);
            }

            return sortUsersByLatest(prev.map(user => {
                if (user.wa_id !== normalizedMessage.wa_id) return user;
                return {
                    ...user,
                    last_message: normalizedMessage.text,
                    unread_count: isSelectedUser ? 0 : (user.unread_count || 0) + 1,
                    updated_at: normalizedMessage.timestamp,
                    last_message_at: normalizedMessage.timestamp
                };
            }));
        });

        if (selectedUserRef.current && normalizedMessage.wa_id === selectedUserRef.current.wa_id) {
            if (normalizedMessage.message_type === "reaction") {
                setWhatsappMessages(prev =>
                    applyReactionOnMessages(prev, {
                        target_message_id: normalizedMessage.target_message_id,
                        reaction: {
                            emoji: (normalizedMessage.provider_response as any)?.reaction?.emoji,
                            wa_id: normalizedMessage.wa_id,
                            message_id: normalizedMessage.target_message_id
                        },
                        reaction_message_id: normalizedMessage.message_id
                    })
                );
                return;
            }

            setWhatsappMessages(prev => {
                const incomingMessageId = normalizedMessage.message_id || normalizedMessage.messageId;
                const hasDuplicate = prev.some(msg => {
                    const existingMessageId = msg.message_id || msg.messageId;
                    return (incomingMessageId && existingMessageId && incomingMessageId === existingMessageId) || msg._id === normalizedMessage._id;
                });

                if (hasDuplicate) return prev;
                return [...prev, normalizedMessage];
            });

            sendWsEvent("mark_whatsapp_read", { wa_id: normalizedMessage.wa_id });
        } else {
            toast.success(`New message from ${normalizedMessage.wa_id}`, {
                position: "bottom-right",
                id: `new-msg-${normalizedMessage.wa_id}`
            });
        }
    };

    const handleMessageRead = (payload: any) => {
        const readPayload = payload?.data || payload;
        if (!selectedUserRef.current || readPayload?.wa_id !== selectedUserRef.current.wa_id) return;
        setWhatsappMessages(prev => prev.map(msg => ({ ...msg, is_read: 1 })));
    };

    useEffect(() => {
        if (!isAuthenticated) {
            setIsConnected(false);
            setSocket(null);
            socketRef.current?.close();
            socketRef.current = null;
            return;
        }

        const token = localStorage.getItem('token');
        if (!token) {
            setIsConnected(false);
            setSocket(null);
            socketRef.current?.close();
            socketRef.current = null;
            return;
        }

        const baseHost = "https://api1.v2.production.smartmatrimony.ai"
        // const baseHost = (localStorage.getItem("apiHost") || window.location.origin || "http://localhost:8000")
            .toString()
            .replace(/\/$/, "");
        const wsBase = baseHost.replace(/^http/, "ws");
        const wsUrl = `${wsBase}/ws/${encodeURIComponent(token)}`;
        let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
        let reconnectAttempts = 0;
        let currentWs: WebSocket | null = null;
        let shouldReconnect = true;

        const connect = () => {
            const ws = new WebSocket(wsUrl);
            currentWs = ws;
            socketRef.current = ws;
            setSocket(ws);

            ws.onopen = () => {
                setIsConnected(true);
                setSocketError(null);
                reconnectAttempts = 0;
                // ws.send(JSON.stringify(buildEnvelope("join_whatsapp_admin", {})));
                usersRequestRef.current = { search: userSearch, offset: 0, append: false };
                setUsersLoading(true);
                setUsersHasMore(true);
                ws.send(JSON.stringify(buildEnvelope("get_whatsapp_users", {
                    search: userSearch || null,
                    limit: usersLimitRef.current,
                    offset: 0
                })));
                // ws.send(JSON.stringify(buildEnvelope("get_whatsapp_templates", {})));
            };

            ws.onclose = (closeEvent) => {
                setIsConnected(false);

                if (closeEvent.code === 1008) {
                    const reason = closeEvent.reason || "Authentication failed";
                    setSocketError({
                        title: "WhatsApp Authentication Error",
                        message: reason === "Invalid token" ? "Your session is invalid. Please log in again." : reason,
                        code: closeEvent.code
                    });
                    toast.error("WhatsApp authentication failed");
                    return;
                }

                if (!shouldReconnect && closeEvent.code !== 1000) {
                    setSocketError({
                        title: "WhatsApp Connection Closed",
                        message: closeEvent.reason || "Connection closed unexpectedly.",
                        code: closeEvent.code
                    });
                }

                if (!shouldReconnect) return;
                reconnectAttempts += 1;
                const retryDelay = Math.min(30000, 1000 * Math.pow(2, Math.max(0, reconnectAttempts - 1)));
                reconnectTimer = setTimeout(connect, retryDelay);
            };

            ws.onerror = (error) => {
                console.error("Whatsapp WS Error:", error);
                setSocketError({
                    title: "WhatsApp Connection Error",
                    message: "A network or websocket error occurred. Please retry.",
                });
            };

            ws.onmessage = (event) => {
                try {
                    const packet = JSON.parse(event.data);
                    const eventName = packet?.event || packet?.action || packet?.type;
                    const payload = packet?.data ?? packet?.payload ?? packet;
                    if (eventName === "whatsapp_users" || (payload?.users && Array.isArray(payload.users))) {
                        console.log(event.data)
                        handleUsersResponse(packet);
                        return;
                    }

                    if (eventName === "whatsapp_messages" || (Array.isArray(payload?.messages) || Array.isArray(payload?.message))) {
                        console.log(event.data)
                        handleMessagesResponse(packet);
                        return;
                    }

                    if (eventName === "whatsapp_templates" || payload?.templates) {
                        handleTemplatesResponse(packet);
                        return;
                    }

                    if (eventName === "whatsapp_unread_summary") {
                        handleUnreadSummaryResponse(packet);
                        return;
                    }

                    if (eventName === "whatsapp_template_res") {
                        const ok = payload?.success === true || payload?.status === "success";
                        if (ok) {
                            toast.success(payload?.message || "Template sent");
                        } else {
                            toast.error(payload?.error || payload?.message || "Template send failed");
                        }
                        if (payload?.data?.wa_id && (payload?.data?.body || payload?.data?.message)) {
                            handleNewMessage(payload.data);
                        }
                        return;
                    }

                    if (eventName === "new_whatsapp_message" || (payload?.wa_id && (payload?.text || payload?.message || payload?.body))) {
                        handleNewMessage(payload);
                        return;
                    }

                    if (eventName === "whatsapp_message_sent" || (payload?.wa_id && (payload?.text || payload?.message || payload?.body))) {
                        handleNewMessage(payload);
                        return;
                    }

                    if (eventName === "whatsapp_message_read") {
                        handleMessageRead(packet);
                        return;
                    }

                    if (eventName === "whatsapp_reaction_updated") {
                        setWhatsappMessages(prev =>
                            applyReactionOnMessages(prev, {
                                target_message_id: payload?.target_message_id,
                                reactions: payload?.reactions,
                                reaction: payload?.reaction,
                                reaction_message_id: payload?.reaction_message_id
                            })
                        );
                        return;
                    }

                    if (eventName === "whatsapp_template_messages") {
                        console.log(event.data)
                        handleTemplateMessagesResponse(payload);
                        return;
                    }

                    if (eventName === "whatsapp_error" || eventName === "error") {
                        const errorMessage = payload?.error || payload?.message || packet?.error || "Unknown WhatsApp error";
                        setSocketError({
                            title: "WhatsApp Error",
                            message: errorMessage
                        });
                        toast.error(errorMessage);
                        console.error("Whatsapp WS Error Payload:", payload);
                    }
                } catch (error) {
                    console.error("Failed to parse WS message", error);
                }
            };
        };

        connect();

        return () => {
            shouldReconnect = false;
            if (reconnectTimer) clearTimeout(reconnectTimer);
            if (currentWs && currentWs.readyState === WebSocket.OPEN) {
                currentWs.send(JSON.stringify(buildEnvelope("leave_whatsapp_admin", {})));
            }
            currentWs?.close();
            socketRef.current = null;
        };
    }, [isAuthenticated, reconnectSignal]);

    useEffect(() => {
        const triggerReconnectIfNeeded = () => {
            const ws = socketRef.current;
            if (!isAuthenticated) return;
            if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
                setReconnectSignal(prev => prev + 1);
            }
        };

        const handleVisibility = () => {
            if (document.visibilityState === "visible") {
                triggerReconnectIfNeeded();
            }
        };

        window.addEventListener("focus", triggerReconnectIfNeeded);
        window.addEventListener("online", triggerReconnectIfNeeded);
        document.addEventListener("visibilitychange", handleVisibility);

        return () => {
            window.removeEventListener("focus", triggerReconnectIfNeeded);
            window.removeEventListener("online", triggerReconnectIfNeeded);
            document.removeEventListener("visibilitychange", handleVisibility);
        };
    }, [isAuthenticated]);

    useEffect(() => {
        if (!socket || !selectedUser || !isConnected) return;
        if (socket.readyState !== WebSocket.OPEN) return;

        sendWsEvent("get_whatsapp_messages", { wa_id: selectedUser.wa_id });
        sendWsEvent("mark_whatsapp_read", { wa_id: selectedUser.wa_id });
        setUsers(prev => prev.map(u => u.wa_id === selectedUser.wa_id ? { ...u, unread_count: 0 } : u));
    }, [socket, selectedUser, isConnected]);

    const selectUser = (user: ChatUser) => {
        setSelectedUser(user);
        localStorage.setItem("currentSelectedUser", JSON.stringify(user));
    };

    const clearSelectedUser = () => {
        setSelectedUser(null);
        setWhatsappMessages([]);
        localStorage.removeItem("currentSelectedUser");
    };

    const sendMessage = (text: string) => {
        if (!socket || !selectedUser) return;
        if (!text.trim()) {
            toast.error("Message cannot be empty");
            return;
        }

        const messageData = {
            wa_id: selectedUser.wa_id,
            message: text,
            name: selectedUser.name,
            messageId: Date.now().toString(),
            timestamp: Date.now()
        };

        const tempMessage: ChatMessage = {
            _id: `temp-${Date.now()}`,
            wa_id: selectedUser.wa_id,
            from: "admin",
            to: selectedUser.wa_id,
            type: "text",
            text,
            message: text,
            timestamp: new Date().toISOString(),
            sender: "admin",
            is_read: 0,
            is_delivered: 0
        };

        // setWhatsappMessages(prev => [...prev, tempMessage]);
        sendWsEvent("send_whatsapp_message", messageData);
    };

    const startNewChat = async (user: ChatUser): Promise<boolean> => {
        if (!socket) return false;

        try {
            setUsers(prev => {
                if (prev.find(u => u.wa_id === user.wa_id)) return prev;
                return [user, ...prev];
            });

            selectUser(user);

            sendWsEvent("send_whatsapp_message", {
                wa_id: user.wa_id,
                text: user.last_message || "Welcome",
                type: "text"
            });

            return true;
        } catch (error) {
            console.error(error);
            return false;
        }
    };

    const sendWhatsappTemplate = async ({
        user,
        templateKey,
        parameters
    }: {
        user: ChatUser;
        templateKey: string;
        parameters: Record<string, string>;
    }): Promise<boolean> => {
        if (!socket || !templateKey || !user?.wa_id) return false;

        try {
            setUsers(prev => {
                if (prev.find(u => u.wa_id === user.wa_id)) return prev;
                return [user, ...prev];
            });

            selectUser(user);

            const schema = templates[templateKey];
            sendWsEvent("whatsapp_template", {
                wa_id: user.wa_id,
                template_key: templateKey,
                template_name: schema?.template_name,
                language_code: schema?.language_code,
                parameters
            });

            return true;
        } catch (error) {
            console.error(error);
            return false;
        }
    };

    const refreshUsers = () => {
        usersRequestRef.current = { search: userSearch, offset: 0, append: false };
        setUsersLoading(true);
        setUsersHasMore(true);
        sendWsEvent("get_whatsapp_users", {
            search: userSearch || null,
            limit: usersLimitRef.current,
            offset: 0
        });

        if (!selectedUser) return;
        sendWsEvent("get_whatsapp_messages", { wa_id: selectedUser.wa_id });

    };

    const fetchTemplates = useCallback(() => {
        sendWsEvent("get_whatsapp_templates", {});
    }, []);

    const fetchUnreadSummary = useCallback(() => {
        sendWsEvent("get_whatsapp_unread_summary", { limit_users: 25 });
    }, []);

    const searchUsers = (search: string) => {
        setUserSearch(search);
        usersRequestRef.current = { search, offset: 0, append: false };
        setUsersLoading(true);
        setUsersHasMore(true);
        sendWsEvent("get_whatsapp_users", {
            search: search || null,
            limit: usersLimitRef.current,
            offset: 0
        });
    };

    const loadMoreUsers = () => {
        if (usersLoading || !usersHasMore) return;
        const nextOffset = usersOffset + usersLimitRef.current;
        usersRequestRef.current = { search: userSearch, offset: nextOffset, append: true };
        setUsersLoading(true);
        sendWsEvent("get_whatsapp_users", {
            search: userSearch || null,
            limit: usersLimitRef.current,
            offset: nextOffset
        });
    };

    const handleTemplateMessagesResponse = (payload: any) => {
        const records = payload?.records || payload?.data?.records || [];
        const total = payload?.total ?? payload?.data?.total ?? payload?.count ?? payload?.data?.count ?? 0;
        setTemplateMessages(Array.isArray(records) ? records : []);
        setTemplateMessagesTotal(typeof total === "number" ? total : 0);
        setTemplateMessagesLoading(false);
    };

    const fetchTemplateMessages = useCallback((filters: TemplateMessagesFilters = {}, limit = 25, offset = 0) => {
        setTemplateMessagesLoading(true);
        sendWsEvent("get_whatsapp_template_messages", {
            limit,
            offset,
            template_key: filters.template_key || undefined,
            recipient_user_id: filters.recipient_user_id || undefined,
            recipient_phone: filters.recipient_phone || undefined,
            source: filters.source || undefined,
            status: filters.status || undefined,
            category: filters.category || undefined,
        });
    }, []);

    const clearSocketError = () => {
        setSocketError(null);
    };

    const value = useMemo(
        () => ({
            socket,
            users,
            isConnected,
            totalUnreadCount,
            templates,
            templatesCount,
            unreadSummary,
            socketError,
            selectedUser,
            whatsappMessages,
            usersLoading,
            usersHasMore,
            userSearch,
            templateMessages,
            templateMessagesTotal,
            templateMessagesLoading,
            selectUser,
            clearSelectedUser,
            sendMessage,
            startNewChat,
            sendWhatsappTemplate,
            refreshUsers,
            fetchTemplates,
            fetchUnreadSummary,
            searchUsers,
            loadMoreUsers,
            clearSocketError,
            setUsers,
            fetchTemplateMessages
        }),
        [socket, users, isConnected, totalUnreadCount, templates, templatesCount, unreadSummary, socketError, selectedUser, whatsappMessages, usersLoading, usersHasMore, userSearch, usersOffset, templateMessages, templateMessagesTotal, templateMessagesLoading]
    );

    return (
        <WhatsappContext.Provider value={value}>
            {children}
        </WhatsappContext.Provider>
    );
};

export const useWhatsapp = () => {
    const context = useContext(WhatsappContext);
    if (context === undefined) {
        throw new Error('useWhatsapp must be used within a WhatsappProvider');
    }
    return context;
};
